<script>
  import { FetchError, fetchTransactionData } from "../../../../../utils/getData.js";
  import { getContext } from "svelte"
  import { useQuery, useQueryClient } from "@sveltestack/svelte-query"
  import { useIsInputAllowed } from "../../../../../hooks/useIsInputAllowed.js"
  import { postStartEndData, reset } from "../../../../../utils/putData.js"

  // props
  /**
   * @type {string}
   */
  export let projectId

  /**
   * This prop determines transactions to be fetched.
   * If a timestamp (format: yyyyMMdd-HHmmss) is given, fetch transactions registered after the timestamp.
   * If an empty string is given, fetch all transactions.
   * @type {string}
   */
  export let downloadTimestamp

  /**
   * This prop will determine at what interval the data will be refetched.
   * Set this to "false" to disable refetching, or provide refetching interval in milliseconds.
   * @type {false|number}
   */
  export let refetchInterval = false

  /**
   * This prop controls whether to fetch transactions
   */
  export let enabled = false

  // error handlers
  const {handleNonBlockingFetchErrors} = getContext("fetchErrorHandler")

  const {isInputAllowed} = useIsInputAllowed(projectId)

  const queryClient = useQueryClient()

  const queryKey = `/api/projects/${projectId}/transactions`

  const transactionsQueryResult = useQuery(
    queryKey,
    () => fetchTransactionData(projectId, downloadTimestamp),
    {
      onError: err => {
        console.log(err)
        if (err instanceof FetchError) {
          handleNonBlockingFetchErrors(err.error)
        }
      },
      retry: false,
      enabled,
      refetchInterval,
    }
  )
  // reset query options to react on downloadTimeStamp change
  $: {
    transactionsQueryResult.setOptions(
      queryKey,
      () => fetchTransactionData(projectId, downloadTimestamp),
      {
        onError: err => {
          console.log(err)
          if (err instanceof FetchError) {
            handleNonBlockingFetchErrors(err.error)
          }
        },
        retry: false,
        enabled,
        refetchInterval,
      }
    )
  }

  function reloadTransactions() {
    $transactionsQueryResult.refetch()
  }

  async function resetTransactions() {
    try {
      await reset(projectId)
      await $transactionsQueryResult.refetch()
    } catch (e) {
      if (e instanceof FetchError) {
        handleNonBlockingFetchErrors(e.error)
      }
    }
  }

  async function postTransaction(transaction, revalidateAfter = 0) {
    // save the "enabled" state
    const originalEnabled = enabled

    // cancel the ongoing query and pause the refetch for the moment
    await queryClient.cancelQueries(queryKey)
    enabled = false

    // inject removeAt into transactions for the DispatchingView functionality
    //   of keeping the completed tasks for a while even if "hide completed operations" setting is on.
    const now = Date.now()
    const injected = Object.fromEntries(Object.entries(transaction).map(entry => [entry[0], {...entry[1], _removeAt: now + revalidateAfter}]))

    // insert the transaction into the cache (optimistic update)
    // the cache will be revalidated later, so the transaction can disappear in case of errors
    queryClient.setQueryData(queryKey, old => ({...old, ...injected}))
    try {
      await postStartEndData(projectId, JSON.stringify(transaction))
    } catch (e) {
      if (e instanceof FetchError) {
        handleNonBlockingFetchErrors(e.error)
      }
    }

    // re-enable refetching
    setTimeout(async () => {
      enabled = originalEnabled
      await queryClient.invalidateQueries(queryKey)
    }, revalidateAfter + 500)
  }
</script>

<slot
  transactions={$transactionsQueryResult.data ?? {}}
  postTransaction={postTransaction}
  reloadTransactions={reloadTransactions}
  resetTransactions={resetTransactions}
  isInputAllowed={$isInputAllowed}
/>